'use client';
import React from 'react';
import { QRType, QRContentValues } from './QRGeneratorClient';

interface QRContentFormProps {
  type: QRType;
  values: QRContentValues;
  onChange: (values: QRContentValues) => void;
  validationError: string;
  onClearError: () => void;
}

function Field({
  label,
  helper,
  error,
  children,
}: {
  label: string;
  helper?: string;
  error?: string;
  children: React.ReactNode;
}) {
  return (
    <div className="space-y-1.5">
      <label className="block font-semibold text-foreground" style={{ fontSize: '13px' }}>{label}</label>
      {helper && <p className="text-muted-foreground" style={{ fontSize: '12px' }}>{helper}</p>}
      {children}
      {error && <p className="text-rose-500" style={{ fontSize: '12px', fontWeight: 500 }}>{error}</p>}
    </div>
  );
}

export default function QRContentForm({ type, values, onChange, validationError, onClearError }: QRContentFormProps) {
  const update = (partial: Partial<QRContentValues>) => {
    onChange({ ...values, ...partial });
    onClearError();
  };

  const renderFields = () => {
    switch (type) {
      case 'url':
        return (
          <Field label="Website URL" helper="Enter the full URL. https:// will be added automatically if missing." error={validationError}>
            <div className="relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" style={{ fontSize: '13px', fontWeight: 500 }}>https://</span>
              <input
                type="text"
                className="input-field"
                style={{ paddingLeft: '72px' }}
                placeholder="knowourindia.com"
                value={values.url.replace(/^https?:\/\//, '')}
                onChange={e => update({ url: e.target.value })}
              />
            </div>
          </Field>
        );

      case 'text':
        return (
          <Field label="Text Content" helper="Enter any text to encode — messages, notes, or any plain text." error={validationError}>
            <textarea
              className="input-field resize-none"
              rows={4}
              placeholder="Enter your text here..."
              value={values.text}
              onChange={e => update({ text: e.target.value })}
            />
          </Field>
        );

      case 'whatsapp':
        return (
          <div className="space-y-4">
            <Field label="WhatsApp Phone Number" helper="Include country code (e.g. +91 for India)." error={validationError}>
              <input
                type="tel"
                className="input-field"
                placeholder="+91 98765 43210"
                value={values.whatsappPhone}
                onChange={e => update({ whatsappPhone: e.target.value })}
              />
            </Field>
            <Field label="Pre-filled Message" helper="Optional message that will be pre-filled in WhatsApp.">
              <textarea
                className="input-field resize-none"
                rows={3}
                placeholder="Hello! I'd like to connect with you."
                value={values.whatsappMessage}
                onChange={e => update({ whatsappMessage: e.target.value })}
              />
            </Field>
          </div>
        );

      case 'phone':
        return (
          <Field label="Phone Number" helper="Include country code for international numbers." error={validationError}>
            <input
              type="tel"
              className="input-field"
              placeholder="+91 98765 43210"
              value={values.phone}
              onChange={e => update({ phone: e.target.value })}
            />
          </Field>
        );

      case 'email':
        return (
          <div className="space-y-4">
            <Field label="Email Address" helper="The recipient's email address." error={validationError}>
              <input
                type="email"
                className="input-field"
                placeholder="contact@company.com"
                value={values.emailTo}
                onChange={e => update({ emailTo: e.target.value })}
              />
            </Field>
            <Field label="Subject" helper="Optional email subject line.">
              <input
                type="text"
                className="input-field"
                placeholder="Inquiry about your services"
                value={values.emailSubject}
                onChange={e => update({ emailSubject: e.target.value })}
              />
            </Field>
            <Field label="Body" helper="Optional pre-filled email body.">
              <textarea
                className="input-field resize-none"
                rows={3}
                placeholder="Hi, I'd like to learn more about..."
                value={values.emailBody}
                onChange={e => update({ emailBody: e.target.value })}
              />
            </Field>
          </div>
        );

      case 'maps':
        return (
          <Field label="Location / Address" helper="Enter a place name, full address, or coordinates." error={validationError}>
            <input
              type="text"
              className="input-field"
              placeholder="India Gate, New Delhi, India"
              value={values.mapsQuery}
              onChange={e => update({ mapsQuery: e.target.value })}
            />
          </Field>
        );

      case 'wifi':
        return (
          <div className="space-y-4">
            <Field label="Network Name (SSID)" helper="The exact name of the WiFi network." error={validationError}>
              <input
                type="text"
                className="input-field"
                placeholder="NovaMax_Office_5G"
                value={values.wifiSsid}
                onChange={e => update({ wifiSsid: e.target.value })}
              />
            </Field>
            <Field label="Password" helper="Leave empty for open networks.">
              <input
                type="text"
                className="input-field"
                placeholder="WiFi password"
                value={values.wifiPassword}
                onChange={e => update({ wifiPassword: e.target.value })}
              />
            </Field>
            <Field label="Security Type">
              <div className="flex gap-2">
                {(['WPA', 'WEP', 'nopass'] as const).map(enc => (
                  <button
                    key={`wifi-enc-${enc}`}
                    onClick={() => update({ wifiEncryption: enc })}
                    className={`flex-1 py-2.5 rounded-xl border text-sm font-semibold transition-all duration-150 ${
                      values.wifiEncryption === enc
                        ? 'gradient-primary text-white border-transparent shadow-blue'
                        : 'border-border text-muted-foreground hover:border-primary/30 hover:text-foreground'
                    }`}
                  >
                    {enc === 'nopass' ? 'Open' : enc}
                  </button>
                ))}
              </div>
            </Field>
          </div>
        );

      case 'upi':
        return (
          <div className="space-y-4">
            <div className="flex items-center gap-2 gradient-blue-soft rounded-2xl px-4 py-3 border border-border">
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="var(--primary)" strokeWidth="1.5" strokeLinecap="round">
                <circle cx="8" cy="8" r="6.5" />
                <path d="M8 5.5v3M8 10.5v.5" />
              </svg>
              <p style={{ fontSize: '12px', color: 'var(--primary)', fontWeight: 500 }}>
                UPI QR codes work with PhonePe, Google Pay, Paytm, and all BHIM-UPI apps.
              </p>
            </div>
            <Field label="UPI ID" helper="Your registered UPI ID (e.g. name@okaxis)." error={validationError}>
              <input
                type="text"
                className="input-field"
                placeholder="yourname@okaxis"
                value={values.upiId}
                onChange={e => update({ upiId: e.target.value })}
              />
            </Field>
            <Field label="Payee Name" helper="Name displayed to the payer.">
              <input
                type="text"
                className="input-field"
                placeholder="NovaMax Studios"
                value={values.upiName}
                onChange={e => update({ upiName: e.target.value })}
              />
            </Field>
            <div className="grid grid-cols-2 gap-4">
              <Field label="Amount (₹)" helper="Optional. Leave empty for any amount.">
                <input
                  type="number"
                  className="input-field"
                  placeholder="499"
                  value={values.upiAmount}
                  onChange={e => update({ upiAmount: e.target.value })}
                />
              </Field>
              <Field label="Note" helper="Optional transaction note.">
                <input
                  type="text"
                  className="input-field"
                  placeholder="Service payment"
                  value={values.upiNote}
                  onChange={e => update({ upiNote: e.target.value })}
                />
              </Field>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="bg-card rounded-3xl border border-border shadow-card p-5">
      <h2 className="font-semibold text-foreground mb-4" style={{ fontSize: '15px' }}>Content</h2>
      {renderFields()}
    </div>
  );
}